import { Box, Paper, Typography, useTheme, Slider, Divider, LinearProgress } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBuilding, FaRecycle } from "react-icons/fa";
import { LuLayers, LuFootprints, LuSlidersHorizontal, LuAward, LuTableProperties } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import MetricCard from "../../app_components/MetricCard";
import AdvancedSankey from "../../components/chart/AdvancedSankey";
import { MainContainer } from "../../components/container/MainContainer";
import LocalLoading from "../../components/dialog/LocalLoading";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import AgGrid from "../../components/grid/AgGrid";
import { defaultColDef, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { Circularity } from "../../db_access/circularity";
import { EnvFactor } from "../../db_access/env_factor";
import { calculate_combined_data } from "../../functions/CircularityFunctions";
import routing from "../../routing";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";

const ProjectSupplierPortal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  
  const { selectedProjects, refresh } = useContextProvider();

  const [activeTab, setActiveTab] = useState("GWP");
  const [materials, setMaterials] = useState([]);
  const [circularities, setCircularities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtering thresholds
  const [maxGwp, setMaxGwp] = useState(1000000);
  const [minMci, setMinMci] = useState(0);

  // Redirect to dashboard if no project is selected
  useMemo(() => {
    if (!selectedProjects || selectedProjects.length === 0) {
      navigate(routing.sector.projects.dashboard);
    }
  }, [selectedProjects, navigate]);

  const selectedProject = selectedProjects[0] || {};

  // Fetch materials and circularities
  useEffect(() => {
    if (!selectedProject.id) return;
    setLoading(true);
    EnvFactor.get_materials_by_project_id(selectedProject.id)
      .then((res) => res.json())
      .then(async (materialsData) => {
        setMaterials(materialsData);
        if (materialsData && materialsData.length > 0) {
          const circsRes = await Circularity.get_by_env_factor_ids(materialsData.map((m) => m.id));
          const circsData = await circsRes.json();
          setCircularities(circsData.data || []);
        } else {
          setCircularities([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading project supplier portal data:", err);
        setLoading(false);
      });
  }, [selectedProject.id, refresh]);

  // Combine and calculate circularity indexes
  const combinedData = useMemo(() => {
    if (materials.length === 0) {
      return { 
        combined_circularities: [], 
        gwp_total_sum: 0, 
        percentage_product_mci: 0,
        supplierCount: 0
      };
    }

    const t0 = materials.map((m) => {
      const c = circularities.find((circ) => String(circ.env_id) === String(m.id));
      return {
        ...m,
        ...(c || {}),
        description: m.description || c?.description || t("Unknown Material"),
      };
    });

    const calculated = calculate_combined_data(t0);

    const uniqueSuppliers = new Set(
      materials
        .map((m) => m.supplier_name && m.supplier_name.trim() ? m.supplier_name.trim() : null)
        .filter(Boolean)
    );

    return {
      ...calculated,
      supplierCount: uniqueSuppliers.size || (materials.some(m => !m.supplier_name) ? 1 : 0)
    };
  }, [materials, circularities, t]);

  // Max values for filters
  const maxGwpValue = useMemo(() => {
    if (combinedData.combined_circularities.length === 0) return 1000;
    return Math.max(...combinedData.combined_circularities.map(m => Number(m.gwp_total) || 0), 100);
  }, [combinedData.combined_circularities]);

  // Sync maxGwp state when materials data is loaded
  useEffect(() => {
    if (maxGwpValue > 0 && maxGwp === 1000000) {
      setMaxGwp(maxGwpValue);
    }
  }, [maxGwpValue]);

  // Filtered materials (applying both Min MCI and Max GWP filters)
  const filteredMaterials = useMemo(() => {
    return combinedData.combined_circularities.filter(m => {
      const gwpVal = Number(m.gwp_total) || 0;
      const mciVal = Number(m.percentage_mci) || 0;
      // Database mci is 0-1, so compare with minMci / 100
      return gwpVal <= maxGwp && mciVal >= minMci / 100;
    });
  }, [combinedData.combined_circularities, maxGwp, minMci]);

  // Supplier scorecard aggregation for Project Materials (based on filteredMaterials)
  const supplierScorecard = useMemo(() => {
    const suppliersMap = new Map();

    filteredMaterials.forEach((m) => {
      const rawSupplier = m.supplier_name || m.supplier;
      const name = rawSupplier && rawSupplier.trim() ? rawSupplier.trim() : t("Unknown Supplier");
      const gwp = Number(m.gwp_total) || 0;
      const mci = Number(m.percentage_mci) || 0;

      if (!suppliersMap.has(name)) {
        suppliersMap.set(name, {
          name,
          materialsCount: 0,
          totalGwp: 0,
          mciSum: 0,
          mciCount: 0
        });
      }

      const entry = suppliersMap.get(name);
      entry.materialsCount += 1;
      entry.totalGwp += gwp;
      if (m.percentage_mci !== undefined && m.percentage_mci !== null) {
        entry.mciSum += mci;
        entry.mciCount += 1;
      }
    });

    const rawScorecard = Array.from(suppliersMap.values()).map((s) => {
      // Scale average MCI to 0-100 percentage
      const avgMci = s.mciCount > 0 ? (s.mciSum / s.mciCount) * 100 : 0;
      return {
        ...s,
        avgMci
      };
    });

    const supplierGwps = rawScorecard.map(s => s.totalGwp);
    const avgGwpLimit = supplierGwps.length > 0 ? (supplierGwps.reduce((a, b) => a + b, 0) / supplierGwps.length) : 0;

    return rawScorecard.map((s) => {
      // Grade Rating based on dataset average GWP
      let grade = "C";
      let gradeColor = isDark ? "#fbbf24" : "#d97706";
      
      const isLowCarbon = s.totalGwp <= avgGwpLimit * 0.8;
      const isHighCarbon = s.totalGwp >= avgGwpLimit * 1.5;

      if (s.avgMci >= 75 && isLowCarbon) {
        grade = "A+";
        gradeColor = isDark ? "#34d399" : "#16a34a";
      } else if (s.avgMci >= 60) {
        grade = "B";
        gradeColor = isDark ? "#4ade80" : "#22c55e";
      } else if (s.avgMci < 30 || isHighCarbon) {
        grade = "D";
        gradeColor = isDark ? "#f87171" : "#dc2626";
      }

      return {
        ...s,
        grade,
        gradeColor
      };
    }).sort((a, b) => b.avgMci - a.avgMci || a.totalGwp - b.totalGwp);
  }, [filteredMaterials, isDark, t]);

  // Nivo Sankey Data Preparation
  const sankeyData = useMemo(() => {
    const nodesMap = new Map();
    const links = [];

    const isGwpMode = activeTab === "GWP";
    const destId = isGwpMode ? "dest:GWP" : "dest:MCI";
    const destLabel = isGwpMode ? t("Total GWP") : t("Circularity");

    if (filteredMaterials.length === 0) {
      return { nodes: [], links: [] };
    }

    // Add final destination node
    nodesMap.set(destId, { 
      id: destId, 
      label: destLabel, 
      color: isDark ? "#c084fc" : "#a78bfa" // Purple
    });

    filteredMaterials.forEach((m) => {
      const rawSupplier = m.supplier_name || m.supplier;
      const supplierName = rawSupplier && rawSupplier.trim() ? rawSupplier.trim() : t("Unknown Supplier");
      const supplierId = `supplier:${supplierName}`;
      const materialId = `material:${m.env_id || m.id}`;

      // Value calculation
      let val = 0;
      if (isGwpMode) {
        val = Number(m.gwp_total) || 0;
      } else {
        // Multiplied by 100 for visual weight in Circularity Index mode
        val = (Number(m.percentage_mci) || 0) * 100;
      }
      
      const displayVal = Math.max(0.1, val);

      // Dynamic color coding based on values to color individual links
      let linkColor = "rgba(200, 200, 200, 0.3)";
      if (isGwpMode) {
        const pctOfMax = maxGwpValue > 0 ? (Number(m.gwp_total) || 0) / maxGwpValue : 0;
        if (pctOfMax > 0.75) {
          linkColor = "rgba(239, 68, 68, 0.45)"; // Red
        } else if (pctOfMax > 0.25) {
          linkColor = "rgba(245, 158, 11, 0.4)";  // Orange
        } else {
          linkColor = "rgba(34, 197, 94, 0.35)";  // Green
        }
      } else {
        const mciVal = (Number(m.percentage_mci) || 0) * 100;
        if (mciVal >= 75) {
          linkColor = "rgba(16, 185, 129, 0.45)"; // Emerald
        } else if (mciVal >= 30) {
          linkColor = "rgba(245, 158, 11, 0.4)";  // Orange
        } else {
          linkColor = "rgba(239, 68, 68, 0.4)";   // Red
        }
      }

      if (!nodesMap.has(supplierId)) {
        nodesMap.set(supplierId, { 
          id: supplierId, 
          label: supplierName, 
          color: isDark ? "#60a5fa" : "#3b82f6" // Blue
        });
      }

      if (!nodesMap.has(materialId)) {
        nodesMap.set(materialId, { 
          id: materialId, 
          label: m.description || t("Unnamed Material"), 
          color: isDark ? "#34d399" : "#10b981" // Green
        });
      }

      // Link 1: Supplier -> Material
      links.push({
        source: supplierId,
        target: materialId,
        value: displayVal,
        color: linkColor
      });

      // Link 2: Material -> Destination
      links.push({
        source: materialId,
        target: destId,
        value: displayVal,
        color: linkColor
      });
    });

    return {
      nodes: Array.from(nodesMap.values()),
      links
    };
  }, [filteredMaterials, activeTab, isDark, t, maxGwpValue]);

  const tabOptions = [
    { label: t("Global Warming Potential (GWP)"), value: "GWP" },
    { label: t("Circularity Index (MCI)"), value: "MCI" }
  ];

  // Max value for progress bars
  const maxSupplierGwp = Math.max(...supplierScorecard.map(s => s.totalGwp), 1);

  // Dynamic height calculation for Sankey diagram to handle scaling data
  const sankeyHeight = useMemo(() => {
    return Math.max(400, sankeyData.nodes.length * 35);
  }, [sankeyData.nodes]);

  // ag-Grid Column Definitions
  const gridColumnDefs = useMemo(() => [
    { 
      headerName: t("Supplier Name"), 
      field: "name", 
      ...defaultColDef, 
      cellStyle: { ...leftAlignColDef.cellStyle, fontWeight: 600 } 
    },
    { 
      headerName: t("Eco Grade"), 
      field: "grade", 
      ...defaultColDef,
      cellRenderer: (params) => {
        if (!params.value) return "";
        return (
          <span style={{ 
            padding: "3px 10px", 
            borderRadius: "4px", 
            color: "#fff", 
            fontWeight: 700, 
            backgroundColor: params.data.gradeColor,
            fontSize: "11px"
          }}>
            {params.value}
          </span>
        );
      },
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-start" }
    },
    { 
      headerName: t("Materials Supplied"), 
      field: "materialsCount", 
      ...defaultColDef, 
      ...rightAlignColDef 
    },
    { 
      headerName: t("Total Embodied Carbon (kg CO₂e)"), 
      field: "totalGwp", 
      ...defaultColDef, 
      cellStyle: { ...rightAlignColDef.cellStyle, color: isDark ? "#f87171" : "#dc2626", fontWeight: 600 }, 
      valueFormatter: (params) => params.value ? params.value.toLocaleString() : "0"
    },
    { 
      headerName: t("Average Circularity Index (MCI)"), 
      field: "avgMci", 
      ...defaultColDef, 
      cellStyle: { ...rightAlignColDef.cellStyle, color: isDark ? "#4ade80" : "#16a34a", fontWeight: 600 }, 
      valueFormatter: (params) => params.value !== undefined ? `${params.value.toFixed(1)}%` : "0%"
    }
  ], [isDark, t]);

  if (loading) {
    return <LocalLoading />;
  }

  return (
    <MainContainer>
      {/* Metrics Section */}
      <Box sx={{ mb: 4 }}>
        <Box className="eng-flex-grid">
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Project Materials")}
              value={materials.length}
              icon={LuLayers}
              accent="#6366f1"
              bg="rgba(99, 102, 241, 0.12)"
              type="chain"
              delay={0.05}
            />
          </Box>
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Materials Embodied Carbon")}
              value={combinedData.gwp_total_sum}
              icon={LuFootprints}
              accent="#16a34a"
              bg="rgba(22, 163, 74, 0.12)"
              type="gwp"
              unit="kg CO₂e"
              delay={0.12}
            />
          </Box>
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Project Circularity Index")}
              // Multiply DB 0-1 scale to 0-100 percentage
              value={combinedData.percentage_product_mci * 100}
              icon={FaRecycle}
              accent="#f59e0b"
              bg="rgba(245, 158, 11, 0.12)"
              type="shield"
              unit="%"
              delay={0.19}
            />
          </Box>
          <Box className="eng-metric-item">
            <MetricCard
              title={t("Project Suppliers")}
              value={combinedData.supplierCount}
              icon={FaBuilding}
              accent="#38bdf8"
              bg="rgba(14, 165, 233, 0.12)"
              type="chain"
              delay={0.26}
            />
          </Box>
        </Box>
      </Box>

      {/* Full-width Segmented Tabs directly below the metric cards */}
      <Box sx={{ mb: 4, width: "100%" }}>
        <SegmentedTabs 
          options={tabOptions} 
          selected={activeTab} 
          setSelected={setActiveTab} 
          activeColor={tok.bg.tabActive}
        />
      </Box>

      {/* Box layout side-by-side stretching to 100% width with equal 50/50 flex distribution */}
      <Box 
        sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", md: "row" }, 
          gap: 3, 
          mb: 4, 
          width: "100%" 
        }}
      >
        {/* Filters Card */}
        <Paper 
          sx={{ 
            flex: 1,
            p: 3, 
            borderRadius: "12px", 
            border: "1px solid", 
            borderColor: tok.border.card,
            bgcolor: tok.bg.card,
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
            minWidth: 0
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: tok.text.chartTitle, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LuSlidersHorizontal size={18} color={isDark ? "#4ade80" : "#659d33"} />
            {t("Visualization Filters")}
          </Typography>

          <Divider sx={{ mb: 2.5 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Typography variant="body2" sx={{ color: tok.text.secondary, mb: 0.5, display: "flex", justifyContent: "space-between" }}>
                <span>{t("Minimum Circularity Index")}</span>
                <strong>&ge; {minMci}%</strong>
              </Typography>
              <Slider
                value={minMci}
                onChange={(e, val) => setMinMci(val)}
                min={0}
                max={100}
                sx={{ color: isDark ? "#4ade80" : "#659d33", py: 0.5 }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ color: tok.text.secondary, mb: 0.5, display: "flex", justifyContent: "space-between" }}>
                <span>{t("Maximum Carbon Limit (GWP)")}</span>
                <strong>&le; {maxGwp === 1000000 ? Math.ceil(maxGwpValue) : maxGwp} kg CO₂e</strong>
              </Typography>
              <Slider
                value={maxGwp === 1000000 ? maxGwpValue : maxGwp}
                onChange={(e, val) => setMaxGwp(val)}
                min={0}
                max={Math.ceil(maxGwpValue)}
                sx={{ color: isDark ? "#4ade80" : "#659d33", py: 0.5 }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Scorecard Card */}
        <Paper
          sx={{
            flex: 1,
            p: 3,
            borderRadius: ORG_PALETTE.radius.card,
            border: "1px solid",
            borderColor: tok.border.card,
            bgcolor: tok.bg.card,
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
            minWidth: 0
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: tok.text.chartTitle, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LuAward size={18} color={isDark ? "#f59e0b" : "#d97706"} />
            {t("Supplier Scorecard")}
          </Typography>

          <Divider sx={{ mb: 2.5 }} />

          <Box 
            sx={{ 
              flex: 1, 
              display: "flex", 
              flexDirection: "row", 
              gap: 2, 
              overflowX: "auto", 
              pb: 1.5,
              alignItems: "center"
            }}
          >
            {supplierScorecard.map((s, idx) => (
              <Box 
                key={idx} 
                sx={{ 
                  p: 2, 
                  borderRadius: "8px", 
                  border: "1px solid", 
                  borderColor: isDark ? "#27272a" : "#f1f5f9", 
                  bgcolor: isDark ? "rgba(255,255,255,0.01)" : "#fcfdfe",
                  width: 250,
                  minWidth: 250,
                  flexShrink: 0
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography sx={{ fontWeight: 600, color: tok.text.primary, fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
                    {s.name}
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      px: 1, 
                      py: 0.2, 
                      borderRadius: "4px", 
                      fontSize: "11px", 
                      fontWeight: 700, 
                      color: "#fff", 
                      bgcolor: s.gradeColor
                    }}
                  >
                    {s.grade}
                  </Box>
                </Box>

                <Typography variant="caption" sx={{ color: tok.text.muted, display: "block", mb: 1.5 }}>
                  {s.materialsCount} {s.materialsCount === 1 ? t("material") : t("materials")} supplied
                </Typography>

                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: tok.text.secondary, mb: 0.2 }}>
                    <span>{t("Carbon GWP")}</span>
                    <strong>{s.totalGwp.toLocaleString()} kg</strong>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={(s.totalGwp / maxSupplierGwp) * 100}
                    sx={{
                      height: 5,
                      borderRadius: 2.5,
                      bgcolor: isDark ? "#27272a" : "#e2e8f0",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: isDark ? "#ef4444" : "#dc2626"
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "10px", color: tok.text.secondary, mb: 0.2 }}>
                    <span>{t("Circularity (MCI)")}</span>
                    <strong>{s.avgMci.toFixed(1)}%</strong>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={s.avgMci}
                    sx={{
                      height: 5,
                      borderRadius: 2.5,
                      bgcolor: isDark ? "#27272a" : "#e2e8f0",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: isDark ? "#34d399" : "#16a34a"
                      }
                    }}
                  />
                </Box>
              </Box>
            ))}

            {supplierScorecard.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center", width: "100%" }}>
                <Typography variant="body2" sx={{ color: tok.text.muted }}>{t("No supplier data found.")}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Sankey Diagram Card (Full-width) */}
      <Paper
        variant="Chart Container"
        sx={{
          p: ORG_PALETTE.spacing.chartPad,
          borderRadius: ORG_PALETTE.radius.card,
          border: "1px solid",
          borderColor: tok.border.card,
          bgcolor: tok.bg.card,
          overflow: "hidden",
          mb: 4
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 4, 
            pb: 2, 
            fontWeight: 500, 
            color: tok.text.chartTitle, 
            borderBottom: `1px solid ${tok.border.divider}`,
            textAlign: 'left'
          }}
        >
          {activeTab === "GWP" 
            ? t("Supplier Carbon Flow (GWP)") 
            : t("Supplier Circularity Flow (MCI)")
          }
        </Typography>
        
        <Box sx={{ overflowX: "auto", minWidth: "100%" }}>
          {sankeyData.links.length > 0 ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", paddingX: 20, mb: 1 }}>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: tok.text.secondary }}>{t("Supplier")}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: tok.text.secondary }}>{t("Material")}</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: 600, color: tok.text.secondary }}>
                  {activeTab === "GWP" ? t("Total GWP") : t("Circularity")}
                </Typography>
              </Box>
              <AdvancedSankey 
                data={sankeyData} 
                height={sankeyHeight} 
                nodeThickness={12} 
                nodeSpacing={26}
                margin={{ top: 20, right: 180, bottom: 20, left: 180 }}
                valueLabel={activeTab === "GWP" ? "kg CO₂e" : "%"}
              />
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography sx={{ color: tok.text.muted }}>
                {t("No materials match the selected filters. Try adjusting the sliders.")}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Supplier Performance Directory Table (ag-Grid) styled exactly like Project Dashboard table */}
      <Paper variant="Table Container" sx={{ mt: 4 }}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {t("Supplier Performance Directory")}
            </Typography>
          </TableTitleContainer>
        </TableHeaderContainer>
        <Box sx={{ width: "100%" }}>
          <AgGrid
            columnDefs={gridColumnDefs}
            rowData={supplierScorecard}
            headerCheckbox={false}
            getRowId={(params) => params.data.name}
            domLayout={supplierScorecard.length <= 15 ? "autoHeight" : "normal"}
            maxHeight="858px"
          />
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default ProjectSupplierPortal;
